import { PermissionContext } from '@adornis/abac/policies/SimpleRolePolicy.js';
import {
  CLIENT_SCOPABLE_PERMISSIONS,
  PARTNER_CLIENT_SCOPABLE_PERMISSIONS,
} from '@nsc/nexus-permissions/platform-rbac/nexus-permissions.js';

function getContextsFromScope(
  scope: PermissionContext[] | undefined,
  permissions: readonly string[],
): string[] {
  if (!scope) return [];
  const ids = scope
    .filter(ctx => permissions.includes(ctx.permission))
    .flatMap(ctx => ctx.contexts ?? []);
  return [...new Set(ids)];
}

function addContextToScope(
  scope: PermissionContext[] | undefined,
  permissions: readonly string[],
  contextId: string,
): PermissionContext[] {
  const result = [...(scope ?? [])];
  for (const permission of permissions) {
    const existing = result.find(ctx => ctx.permission === permission);
    if (existing) {
      if (!existing.contexts?.includes(contextId)) {
        existing.contexts = [...(existing.contexts ?? []), contextId];
      }
    } else {
      result.push(new PermissionContext({ permission, contexts: [contextId] }));
    }
  }
  return result;
}

function removeContextFromScope(
  scope: PermissionContext[] | undefined,
  permissions: readonly string[],
  contextId: string,
): PermissionContext[] {
  if (!scope) return [];
  return scope
    .map(ctx => {
      if (!permissions.includes(ctx.permission)) return ctx;
      const remaining = (ctx.contexts ?? []).filter(id => id !== contextId);
      if (remaining.length === 0) return null;
      return new PermissionContext({ _id: ctx._id, permission: ctx.permission, contexts: remaining });
    })
    .filter((ctx): ctx is PermissionContext => ctx !== null);
}

/** Client IDs from {@link CLIENT_SCOPABLE_PERMISSIONS} rows only. */
export function getClientIdsFromScope(scope: PermissionContext[] | undefined): string[] {
  return getContextsFromScope(scope, CLIENT_SCOPABLE_PERMISSIONS);
}

export function addClientToScope(scope: PermissionContext[] | undefined, clientId: string): PermissionContext[] {
  return addContextToScope(scope, CLIENT_SCOPABLE_PERMISSIONS, clientId);
}

export function removeClientFromScope(scope: PermissionContext[] | undefined, clientId: string): PermissionContext[] {
  return removeContextFromScope(scope, CLIENT_SCOPABLE_PERMISSIONS, clientId);
}

/** Partner `clientKey`s from {@link PARTNER_CLIENT_SCOPABLE_PERMISSIONS} only. */
export function getPartnerClientKeysFromScope(scope: PermissionContext[] | undefined): string[] {
  return getContextsFromScope(scope, PARTNER_CLIENT_SCOPABLE_PERMISSIONS);
}

export function addPartnerClientKeyToScope(
  scope: PermissionContext[] | undefined,
  clientKey: string,
): PermissionContext[] {
  return addContextToScope(scope, PARTNER_CLIENT_SCOPABLE_PERMISSIONS, clientKey);
}

export function removePartnerClientKeyFromScope(
  scope: PermissionContext[] | undefined,
  clientKey: string,
): PermissionContext[] {
  return removeContextFromScope(scope, PARTNER_CLIENT_SCOPABLE_PERMISSIONS, clientKey);
}
